import './skeleton.css'

/**
 * Reusable Skeleton component for loading placeholders
 *
 * @param {string} variant - 'text', 'title', 'circle', 'rect', 'card', 'table', 'kpi'
 * @param {number} width - width in px or '%' string
 * @param {number} height - height in px
 * @param {number} count - number of skeleton items to render
 * @param {string} className - additional CSS class
 */
const Skeleton = ({ variant = 'text', width, height, count = 1, className = '' }) => {
    const items = Array.from({ length: count }, (_, i) => i);

    const getStyle = () => {
        const style = {};
        if (width) style.width = typeof width === 'number' ? `${width}px` : width;
        if (height) style.height = typeof height === 'number' ? `${height}px` : height;
        return style;
    };

    return (
        <>
            {items.map((i) => (
                <div
                    key={i}
                    className={`skeleton skeleton_${variant} ${className}`}
                    style={getStyle()}
                />
            ))}
        </>
    );
};

/**
 * Skeleton wrapper for a card-like container
 */
export const SkeletonCard = ({ children, className = '' }) => (
    <div className={`skeleton_card_container ${className}`}>
        {children}
    </div>
);

/**
 * Pre-built skeleton for KPI grid
 */
export const SkeletonKPI = ({ count = 6 }) => (
    <div className="skeleton_kpi_grid">
        {Array.from({ length: count }, (_, i) => (
            <div key={i} className="skeleton_kpi_box">
                <div className="skeleton skeleton_text" style={{ width: '60%', height: '12px' }} />
                <div className="skeleton skeleton_title" style={{ width: '40%', height: '24px' }} />
            </div>
        ))}
    </div>
);

/**
 * Pre-built skeleton for a table
 */
export const SkeletonTable = ({ rows = 5, cols = 6 }) => (
    <div className="skeleton_table_container">
        <div className="skeleton_table_header">
            {Array.from({ length: cols }, (_, i) => (
                <div key={i} className="skeleton skeleton_text" style={{ width: `${60 + Math.random() * 40}%`, height: '12px' }} />
            ))}
        </div>
        {Array.from({ length: rows }, (_, rowI) => (
            <div key={rowI} className="skeleton_table_row">
                {Array.from({ length: cols }, (_, colI) => (
                    <div key={colI} className="skeleton skeleton_text" style={{ width: `${50 + Math.random() * 50}%`, height: '14px' }} />
                ))}
            </div>
        ))}
    </div>
);

/**
 * Pre-built skeleton for MainActions grid
 */
export const SkeletonActions = ({ count = 6 }) => (
    <div className="skeleton_actions_grid">
        {Array.from({ length: count }, (_, i) => (
            <div key={i} className="skeleton_action_box">
                <div className="skeleton skeleton_circle" style={{ width: '44px', height: '44px' }} />
                <div className="skeleton_action_info">
                    <div className="skeleton skeleton_title" style={{ width: '50px', height: '22px' }} />
                    <div className="skeleton skeleton_text" style={{ width: '100px', height: '12px' }} />
                </div>
            </div>
        ))}
    </div>
);

/**
 * Pre-built skeleton for BrokerAlert
 */
export const SkeletonBroker = () => (
    <div className="skeleton_broker_grid">
        {[0, 1].map((i) => (
            <div key={i} className="skeleton_broker_section">
                <div className="skeleton skeleton_text" style={{ width: '80px', height: '16px', marginBottom: '12px' }} />
                {[0, 1, 2].slice(0, i === 0 ? 3 : 2).map((j) => (
                    <div key={j} className="skeleton_broker_card">
                        <div className="skeleton skeleton_circle" style={{ width: '36px', height: '36px' }} />
                        <div style={{ flex: 1 }}>
                            <div className="skeleton skeleton_text" style={{ width: '70%', height: '14px', marginBottom: '6px' }} />
                            <div className="skeleton skeleton_text" style={{ width: '40%', height: '10px' }} />
                        </div>
                    </div>
                ))}
            </div>
        ))}
    </div>
);

export default Skeleton;
